import { downloadFile } from '@api/Files'
import { cover as defaultCover } from './mocks/defaultCover'

const THEME_FOLDER_PREFIX = 'THEME_FOLDER/'

const PROJECT_TITLE_PLACEHOLDER = 'PROJECT-TITLE'
const PROJECT_AUDIENCE_PLACEHOLDER = 'PROJECT-AUDIENCE'

type CoverType = 'default'

export type CoverConfigurationResult = typeof defaultCover

type GetCoverConfigurationProps = {
  coverType: CoverType
  title: string
  departments: string[]
  /**
   * Base URL for cover assets (e.g. window.location.origin).
   * When provided, THEME_FOLDER/... values are resolved via the download API
   * and replaced with the returned storage paths.
   */
  coverAssetsBaseUrl?: string
}

/**
 * Deep-clones and replaces placeholder strings in a value.
 * Replaces PROJECT-TITLE, PROJECT-AUDIENCE, and (when pathMap is set) THEME_FOLDER paths.
 */
function replaceInValue(
  value: unknown,
  replacements: [string, string][],
  pathMap: Map<string, string> | null
): unknown {
  if (typeof value === 'string') {
    let result = value
    for (const [from, to] of replacements) {
      result = result.split(from).join(to)
    }
    if (pathMap?.has(result)) {
      return pathMap.get(result) ?? result
    }
    return result
  }
  if (Array.isArray(value)) {
    return value.map((item) => replaceInValue(item, replacements, pathMap))
  }
  if (value !== null && typeof value === 'object') {
    const result: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) {
      result[k] = replaceInValue(v, replacements, pathMap)
    }
    return result
  }
  return value
}

function collectThemeFolderPaths(value: unknown): Set<string> {
  const paths = new Set<string>()
  function walk(v: unknown): void {
    if (typeof v === 'string') {
      if (v.startsWith(THEME_FOLDER_PREFIX)) paths.add(v)
      return
    }
    if (Array.isArray(v)) {
      v.forEach(walk)
      return
    }
    if (v !== null && typeof v === 'object') {
      Object.values(v).forEach(walk)
    }
  }
  walk(value)
  return paths
}

async function resolveCoverThemePaths(
  coverType: CoverType,
  baseUrl: string,
  cover: typeof defaultCover
): Promise<Map<string, string>> {
  const paths = collectThemeFolderPaths(cover)
  if (paths.size === 0) return new Map()

  const base = baseUrl.replace(/\/$/, '')
  const coversBase = `${base}/images/corporate/covers/${coverType}`

  const results = await Promise.all(
    Array.from(paths).map(async (themePath) => {
      const filename = themePath.slice(THEME_FOLDER_PREFIX.length)
      const url = `${coversBase}/${filename}`
      try {
        const response = ((await downloadFile({ url })) as unknown) as {
          data: { path: string }
        }
        return [themePath, response.data.path] as const
      } catch {
        return [themePath, themePath] as const
      }
    })
  )
  return new Map(results)
}

export const getCoverConfiguration = async ({
  coverType,
  title,
  departments,
  coverAssetsBaseUrl
}: GetCoverConfigurationProps): Promise<CoverConfigurationResult> => {
  const departmentsLabel = Array.isArray(departments)
    ? departments.join(', ')
    : ''
  const replacements: [string, string][] = [
    [PROJECT_TITLE_PLACEHOLDER, title],
    [PROJECT_AUDIENCE_PLACEHOLDER, departmentsLabel]
  ]

  let pathMap: Map<string, string> | null = null
  if (coverAssetsBaseUrl) {
    pathMap = await resolveCoverThemePaths(
      coverType,
      coverAssetsBaseUrl,
      defaultCover
    )
  }

  return replaceInValue(
    defaultCover,
    replacements,
    pathMap
  ) as CoverConfigurationResult
}
